import { useTenantStore } from '@libero/hooks/store/useTenantStore';
import { SelectOption } from '@libero/types/Select';
import { SelectValues } from '@libero/types/Select';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { isNumber } from 'lodash';
import { Map } from 'mapbox-gl';
import { computed } from 'vue';
import { watch } from 'vue';
import { onMounted } from 'vue';
import { reactive, ref } from 'vue';

interface GeocoderResult {
  features: MapboxGeocoder.Result[];
  query: [number, number] | [string];
}

interface Register {
  id: string;
  name: string;
  options: SelectOption[];
  value: string | null;
  searchValue: string | null;
  onUpdate: (newValue: SelectValues) => void;
  onSearch: (newValue: string) => void;
}

interface UseGeocoder {
  latitude?: number;
  longitude?: number;
  handleCoordinates: (coordinates: [number, number]) => void;
  register: () => Register;
}

type OnResultCallback = (coordinates: [number, number]) => void;

export const useGeocoder = (onResult: OnResultCallback): UseGeocoder => {
  const tenantStore = useTenantStore();

  const latitude = ref<number>();
  const longitude = ref<number>();
  const geocoder = ref<MapboxGeocoder>();
  const history = ref<GeocoderResult>();
  const value = ref<string | null>(null);
  const searchValue = ref<string | null>(null);
  const options = ref<SelectOption[]>([]);

  const register = () => {
    const onUpdate = (newValue: SelectValues) => {
      value.value = newValue?.toString() || null;
    };

    const onSearch = (newValue: string) => {
      searchValue.value = newValue;

      if (newValue) {
        geocoder.value?.query(newValue);
      }
    };

    return reactive({
      id: 'geocoder',
      name: 'geocoder',
      options,
      value,
      searchValue,
      onSearch,
      onUpdate,
    });
  };

  const handleCoordinates = (coordinates: [number, number]) => {
    geocoder.value?.query(`${coordinates[1]}, ${coordinates[0]}`);
  };

  const handleResult = (result: GeocoderResult) => {
    history.value = result;

    options.value = result.features.map((feature) => ({
      label: feature.place_name,
      value: feature.place_name,
    }));

    if (result.query && isNumber(result.query[0]) && isNumber(result.query[1])) {
      value.value = result.features[0].place_name;
    }
  };

  const accessToken = computed(() => tenantStore.tenant?.credentials?.mapbox?.access_token);

  watch(value, () => {
    const current = history.value?.features.find((feature) => feature.place_name === value.value);

    if (current) {
      latitude.value = current.center[1];
      longitude.value = current.center[0];

      onResult([current.center[0], current.center[1]]);
    }
  });

  const init = () => {
    if (!accessToken.value) return;

    geocoder.value = new MapboxGeocoder({
      accessToken: accessToken.value,
      reverseGeocode: true,
      countries: 'nl',
      language: 'nl',
      proximity: tenantStore.tenant
        ? {
            latitude: tenantStore.tenant.center.coordinates[1],
            longitude: tenantStore.tenant.center.coordinates[0],
          }
        : undefined,
      bbox: tenantStore.tenant?.bbox,
    });

    geocoder.value.onAdd(undefined as unknown as Map);

    geocoder.value.on('results', handleResult);
  };

  onMounted(init);
  watch(accessToken, init);

  return reactive({
    latitude,
    longitude,
    handleCoordinates,
    register,
  });
};
